import React, { useState, useEffect } from 'react';
import { CheckCircle2, Phone, ArrowRight, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { servicesApi } from '../api/services';
import './Services.css';

const REAL_STEPS = [
  { id: 1, titleAr: 'استشارة أولية', titleEn: 'Initial Consultation', descAr: 'نجتمع معك لفهم احتياجاتك وأهداف مشروعك.', descEn: 'We meet to understand your needs and project goals.' },
  { id: 2, titleAr: 'التصميم والتخطيط', titleEn: 'Design & Planning', descAr: 'نعد الدراسات والتصاميم الأولية لاعتمادها.', descEn: 'We prepare studies and preliminary designs for approval.' },
  { id: 3, titleAr: 'عرض السعر', titleEn: 'Quote', descAr: 'نقدم عرض سعر شفاف ومفصل بدون تكاليف مخفية.', descEn: 'We provide a transparent detailed quote with no hidden costs.' },
  { id: 4, titleAr: 'التنفيذ والمتابعة', titleEn: 'Implementation', descAr: 'نبدأ المشروع بأعلى معايير الجودة.', descEn: 'We start the project with the highest quality standards.' },
  { id: 5, titleAr: 'التسليم والضمان', titleEn: 'Delivery & Warranty', descAr: 'نسلم المشروع في الوقت المحدد مع ضمان شامل.', descEn: 'We deliver on time with a comprehensive warranty.' }
];

const LABEL_KEY = 'ayonk_services_labels_v2';
const DEFAULT_LABELS = {
  pageTitleAr: 'خدماتنا', pageTitleEn: 'Our Services',
  pageSubAr: 'حلول شاملة لجميع احتياجاتك', pageSubEn: 'Comprehensive solutions for all your needs',
  servicesLabelAr: 'ما نقدمه', servicesLabelEn: 'What We Offer'
};

export function Services() {
    const { language } = useLanguage();
    const isRtl = language === 'ar';
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);

    // THE FIX: Pull dynamic labels from localStorage
    const [labels, setLabels] = useState(DEFAULT_LABELS);

    useEffect(() => {
        try {
            const savedLabels = localStorage.getItem(LABEL_KEY);
            if (savedLabels) setLabels(JSON.parse(savedLabels));
        } catch(e) { console.error("Error loading labels", e); }

        let isMounted = true;
        const fetchServices = async () => {
            try {
                const srvRes = await servicesApi.getAll();
                if (isMounted && Array.isArray(srvRes)) {
                    setServices(srvRes.filter(s => s?.isActive).sort((a,b) => (a?.order || 0) - (b?.order || 0)));
                }
            } catch(e) {
                console.error("API Error:", e);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchServices();
        return () => { isMounted = false; };
    }, []);

    const L = (ar, en) => isRtl ? ar : en;

    const getSafeTitle = (s) => {
        if (!s) return '';
        if (isRtl) return s.titleAr || s.title_ar || (typeof s.title === 'string' ? s.title.split('|')[0] : s.title) || '';
        return s.titleEn || s.title_en || (typeof s.title === 'string' ? s.title.split('|')[1] || s.title.split('|')[0] : s.title) || '';
    };

    const getSafeDesc = (s) => {
        if (!s) return '';
        if (isRtl) return s.descAr || s.desc_ar || (typeof s.description === 'string' ? s.description.split('|')[0] : s.description) || '';
        return s.descEn || s.desc_en || (typeof s.description === 'string' ? s.description.split('|')[1] || s.description.split('|')[0] : s.description) || '';
    };

    if (loading) return <div className="lux-loader"><div className="lux-spinner"></div></div>;

    return (
        <main className="lux-srv-page" dir={isRtl ? 'rtl' : 'ltr'}>
            
            <section className="lux-hero-simple">
                <div className="lux-container">
                    <div className="lux-breadcrumb-box">
                        <div className="lux-capsule">
                            <Link to="/" className="lux-home-link">{L('الرئيسية', 'Home')}</Link>
                            <span className="lux-sep">/</span>
                            <span className="lux-current">{isRtl ? labels.pageTitleAr : labels.pageTitleEn}</span>
                        </div>
                    </div>
                    {/* THE FIX: Applying dynamic page titles */}
                    <h1 className="lux-hero-title-simple">{isRtl ? labels.pageTitleAr : labels.pageTitleEn}</h1>
                    <p style={{textAlign: 'center', color: '#d4af37', fontSize: '1.2rem', marginTop: '10px'}}>{isRtl ? labels.pageSubAr : labels.pageSubEn}</p>
                    <div className="lux-hero-dash"></div>
                </div>
            </section>

            <section className="lux-section-white">
                <div className="lux-container">
                    {/* THE FIX: Applying dynamic section label */}
                    <div className="lux-sec-header-center" style={{marginBottom: '40px'}}>
                        <span className="lux-gold-label">{isRtl ? labels.servicesLabelAr : labels.servicesLabelEn}</span>
                    </div>

                    <div className="lux-list-stack">
                        {services.length === 0 ? (
                            <p style={{textAlign: 'center', color: '#888', padding: '50px'}}>{L('جاري تحديث الخدمات...', 'Updating services...')}</p>
                        ) : (
                            services.map((s, i) => (
                                <div key={s?.id || i} className="lux-modern-card">
                                    <div className="lux-card-number-light">{String(i + 1).padStart(2, '0')}</div>
                                    <div className="lux-card-info">
                                        <h3>{getSafeTitle(s)}</h3>
                                        <p>{getSafeDesc(s)}</p>
                                        <div className="lux-card-tags">
                                            <span><CheckCircle2 size={16} /> {L('جودة عالمية', 'Global Quality')}</span>
                                            <span><CheckCircle2 size={16} /> {L('إشراف هندسي', 'Expert Supervision')}</span>
                                        </div>
                                    </div>
                                    <div className="lux-card-action-area">
                                        <Link to="/request-quote" state={{ serviceType: getSafeTitle(s) }} className="lux-btn-elite">
                                            <span>{L('اطلب عرض سعر', 'Request Quote')}</span>
                                            <div className="sweep-effect"></div>
                                        </Link>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </section>

            <section className="lux-section-dark">
                <div className="lux-container">
                    <div className="lux-sec-header-center">
                        <span className="lux-gold-label">{L('منهجية العمل', 'Workflow')}</span>
                        <h2>{L('خطوات تنفيذ مشروعك', 'Project Steps')}</h2>
                    </div>
                    <div className="lux-steps-grid-row">
                        {REAL_STEPS.map((step, i) => (
                            <div key={step.id} className="lux-step-node-box">
                                <div className="lux-node-header">
                                    <span className="lux-node-index">{i + 1}</span>
                                    <div className="lux-node-connector"></div>
                                </div>
                                <h4>{isRtl ? step.titleAr : step.titleEn}</h4>
                                <p>{isRtl ? step.descAr : step.descEn}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="lux-cta-final">
                <div className="lux-container">
                    <div className="lux-cta-box-white">
                        <h2>{L('هل أنت مستعد لبدء مشروعك؟', 'Ready to Start?')}</h2>
                        <div className="lux-cta-btns-group">
                            <Link to="/contact" className="lux-btn-master-black">
                                <span>{L('تواصل معنا الآن', 'Contact Us')}</span>
                                <div className="sweep-effect"></div>
                            </Link>
                            <a href="tel:01022267372" className="lux-btn-phone-rect">
                                <Phone size={20} />
                                <span>01022267372</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

        </main>
    );
}
